import React, { useState, useEffect } from 'react';
import { 
  X, 
  ChevronRight, 
  ExternalLink, 
  Star, 
  Shield, 
  Zap,
  Clock,
  Eye,
  EyeOff 
} from 'lucide-react';

const DiscreteBanner = ({ 
  position = 'bottom', // 'top', 'bottom'
  page = 'home', // 'home', 'chat', 'discover'
  onClose,
  onAdClick,
  isVisible = true 
}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Mock ad data - would come from ad service
  const adData = {
    home: [
      {
        id: 'bank-ad-1',
        type: 'financial',
        title: 'Épargne Sécurisée',
        subtitle: 'Jusqu\'à 8% d\'intérêt annuel',
        brand: 'Equity Bank',
        image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzA2YjZkNCIvPjx0ZXh0IHg9IjIwIiB5PSIyNCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5FPC90ZXh0Pjwvc3ZnPg==',
        cta: 'Découvrir',
        backgroundColor: '#f0f9ff',
        textColor: '#0369a1'
      }
    ],
    chat: [
      {
        id: 'crypto-ad-1',
        type: 'financial',
        title: 'Crypto Facile',
        subtitle: 'Achetez Bitcoin avec M-Pesa',
        brand: 'Binance',
        image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iI2Y1OWUwYiIvPjx0ZXh0IHg9IjIwIiB5PSIyNCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5CPC90ZXh0Pjwvc3ZnPg==',
        cta: 'Essayer',
        backgroundColor: '#fef3c7',
        textColor: '#d97706'
      }
    ],
    discover: [
      {
        id: 'insurance-ad-1',
        type: 'insurance',
        title: 'Protection Famille',
        subtitle: 'Assurance dès 500 KSh/mois',
        brand: 'Jubilee Insurance',
        image: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iIzEwYjk4MSIvPjx0ZXh0IHg9IjIwIiB5PSIyNCIgZm9udC1zaXplPSIxNCIgZm9udC13ZWlnaHQ9ImJvbGQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5KPC90ZXh0Pjwvc3ZnPg==',
        cta: 'En savoir plus',
        backgroundColor: '#ecfdf5',
        textColor: '#047857'
      }
    ]
  };

  useEffect(() => {
    const ads = adData[page] || adData.home;
    if (ads.length > 0) {
      setCurrentAd(ads[Math.floor(Math.random() * ads.length)]);
    }
  }, [page]);

  const handleClose = () => {
    setIsDismissed(true);
    if (onClose) onClose();
  };

  const handleAdClick = (ad) => {
    if (onAdClick) {
      onAdClick(ad);
    }
    // Analytics tracking would go here
    console.log('Ad clicked:', ad.id);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  if (!isVisible || isDismissed || !currentAd) return null;

  const positionClasses = {
    top: 'top-0',
    bottom: 'bottom-16 md:bottom-0' // Account for mobile nav
  };

  return (
    <div className={`fixed left-0 right-0 z-10 ${positionClasses[position]}`}>
      <div 
        className="mx-2 mb-2 rounded-lg shadow-lg backdrop-blur-sm border transition-all duration-300 hover:shadow-xl"
        style={{ 
          backgroundColor: currentAd.backgroundColor + 'f0', // 94% opacity
          borderColor: currentAd.textColor + '30' // 19% opacity
        }}
      >
        {!isMinimized ? (
          // Full banner
          <div className="p-3">
            <div className="flex items-center justify-between">
              {/* Ad Content */}
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {/* Brand Image */}
                <div className="flex-shrink-0">
                  <img 
                    src={currentAd.image} 
                    alt={currentAd.brand}
                    className="w-10 h-10 rounded-lg"
                  />
                </div>
                
                {/* Ad Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 
                      className="font-semibold text-sm truncate"
                      style={{ color: currentAd.textColor }}
                    >
                      {currentAd.title}
                    </h4>
                    <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded text-uppercase font-medium">
                      Pub
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    {currentAd.subtitle}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {currentAd.brand}
                  </p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                {/* CTA Button */}
                <button
                  onClick={() => handleAdClick(currentAd)}
                  className="px-3 py-1.5 text-xs font-medium rounded-full transition-colors hover:opacity-90 flex items-center space-x-1"
                  style={{ 
                    backgroundColor: currentAd.textColor,
                    color: 'white'
                  }}
                  aria-label={`${currentAd.cta} - ${currentAd.title}`}
                >
                  <span>{currentAd.cta}</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
                
                {/* Minimize */}
                <button
                  onClick={handleMinimize}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Réduire la publicité"
                >
                  <EyeOff className="w-4 h-4" />
                </button>
                
                {/* Close */}
                <button
                  onClick={handleClose}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
                  aria-label="Fermer la publicité"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Minimized banner
          <div className="p-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img 
                  src={currentAd.image} 
                  alt={currentAd.brand}
                  className="w-6 h-6 rounded"
                />
                <span className="text-xs text-gray-600 font-medium">
                  {currentAd.brand}
                </span>
                <span className="text-xs text-gray-500 px-1 py-0.5 bg-gray-100 rounded">
                  Pub
                </span>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleMinimize}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  aria-label="Développer la publicité"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={handleClose}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  aria-label="Fermer la publicité"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscreteBanner;